import {
  Autocomplete,
  Button,
  Flex,
  Grid,
  Label,
  ScrollView,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { LayoutContext } from "layout/context/LayoutContext";
import {
  listBranches,
  listCompanies,
  listDevices,
  listMaintenanceRoutines,
} from "graphql/queries";
import SelectSpare from "components/spares/SelectSpare";

const containerStyleMap = {
  width: "100%",
  height: "350px",
};

const centerMap = {
  lat: 6.25184,
  lng: -75.56359,
};

const EquipmentForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const { isLoaded: mapIsLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });
  const [equipmentForm, setEquipmentForm] = useState(initialValues);
  const [companies, setCompanies] = useState({
    list: [],
    loading: false,
  });
  const [branches, setBranches] = useState({
    list: [],
    loading: false,
  });
  const [devices, setDevices] = useState({
    list: [],
    loading: false,
  });
  const [routines, setRoutines] = useState({
    list: [],
    loading: false,
  });
  const [errors, setErrors] = useState({
    name: "",
    // location: "",
    companyID: "",
    branchID: "",
    deviceID: "",
    routineID: "",
  });
  const validateErrors = useErrors({
    name: "isRequired",
    // location: "isRequired",
    companyID: "isRequired",
    branchID: "isRequired",
    // deviceID: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(equipmentForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (
      checkIncorrectValue("companyID", companies.list) ||
      checkIncorrectValue("branchID", branches.list) ||
      (equipmentForm.deviceID &&
        checkIncorrectValue("deviceID", devices.list)) ||
      (equipmentForm.routineID &&
        checkIncorrectValue("routineID", routines.list))
    )
      return;

    if (!hasErrors)
      onSubmit({
        ...equipmentForm,
        deviceID: equipmentForm.deviceID || null,
        routineID: equipmentForm.routineID || null,
      });
  };

  const checkIncorrectValue = (name, list) => {
    if (!list.some((rec) => rec.id === equipmentForm[name])) {
      setErrors((prevSt) => ({
        ...prevSt,
        [name]: strings.validations.incorrectValue,
      }));
      return true;
    }
    return false;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setEquipmentForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeSpares = (spares) => {
    setEquipmentForm((prevSt) => ({
      ...prevSt,
      spares,
    }));
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "companyID") {
      fetchCompanies({ name: value });
      onClearSelect("branchID");
      onClearSelect("routineID");
    }
    if (name === "branchID") {
      fetchBranches({ name: value }, equipmentForm.companyID);
      onClearSelect("deviceID");
    }
    if (name === "routineID") {
      fetchRoutines({ name: value }, equipmentForm.companyID);
    }
    if (name === "deviceID")
      fetchDevices({ name: value }, equipmentForm.branchID);
    setEquipmentForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    setEquipmentForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
    if (name === "companyID") {
      onClearSelect("branchID", value);
      onClearSelect("routineID", value);
      onChangeSpares([]);
    }
    if (name === "branchID") {
      onClearSelect("deviceID", value);
    }
  };

  const onClearSelect = (name, parentId) => {
    const value = "";
    if (name === "companyID") {
      fetchCompanies({ name: value });
      onClearSelect("branchID");
      onClearSelect("routineID");
      onChangeSpares([]);
    }
    if (name === "branchID") {
      if (!parentId) setBranches({ list: [], loading: false });
      else fetchBranches({ name: value }, parentId);
      onClearSelect("deviceID");
    }
    if (name === "routineID") {
      if (!parentId) setRoutines({ list: [], loading: false });
      else fetchRoutines({ name: value }, parentId);
    }
    if (name === "deviceID") {
      if (!parentId) setDevices({ list: [], loading: false });
      else fetchDevices({ name: value }, parentId);
    }
    setEquipmentForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const onChangeMapMarker = (event) => {
    const { latLng } = event;
    setEquipmentForm((prevSt) => ({
      ...prevSt,
      coordsLat: latLng.lat(),
      coordsLng: latLng.lng(),
    }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      name: "",
      location: "",
      companyID: "",
      branchID: "",
      deviceID: "",
      routineID: "",
    });
    setEquipmentForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    fetchBranches({ id: initialValues.branchID }, initialValues.companyID);
    fetchDevices({ id: initialValues.deviceID }, initialValues.branchID);
    fetchRoutines({ id: initialValues.routineID }, initialValues.companyID);
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompanies({ list: [], loading: true });

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies((prev) => ({ ...prev, list: companiesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setCompanies((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchBranches({ id = "", name = "" }, companyId) {
    try {
      setBranches({ list: [], loading: true });
      const branchesData = await API.graphql(
        graphqlOperation(listBranches, {
          limit: !id && !name ? 20 : undefined,
          filter: {
            companyID: { eq: companyId },
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
          },
        })
      );
      const branchesItems = branchesData.data.listBranches.items;
      setBranches((prev) => ({ ...prev, list: branchesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchBranches);
    } finally {
      setBranches((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchRoutines({ id = "", name = "" }, companyId) {
    try {
      setRoutines({ list: [], loading: true });
      const routinesData = await API.graphql(
        graphqlOperation(listMaintenanceRoutines, {
          limit: !id && !name ? 20 : undefined,
          filter: {
            companyID: { eq: companyId },
            and: [
              {
                id: { contains: id || "" },
              },
              {
                name: { contains: name || "" },
              },
            ],
          },
        })
      );
      const routinesItems = routinesData.data.listMaintenanceRoutines.items;
      setRoutines((prev) => ({ ...prev, list: routinesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchRoutines);
    } finally {
      setRoutines((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchDevices({ id = "", name = "" }, branchId) {
    try {
      setDevices({ list: [], loading: true });
      const devicesData = await API.graphql(
        graphqlOperation(listDevices, {
          limit: !id && !name ? 20 : undefined,
          filter: {
            branchID: { eq: branchId },
            and: [
              {
                id: { contains: id || "" },
              },
              {
                name: { contains: name || "" },
              },
            ],
          },
        })
      );
      const devicesItems = devicesData.data.listDevices.items;
      setDevices((prev) => ({ ...prev, list: devicesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchDevices);
    } finally {
      setDevices((prev) => ({ ...prev, loading: false }));
    }
  }

  useEffect(() => {
    setEquipmentForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    fetchBranches({ id: initialValues.branchID }, initialValues.companyID);
    fetchDevices({ id: initialValues.deviceID }, initialValues.branchID);
    fetchRoutines({ id: initialValues.routineID }, initialValues.companyID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid templateColumns={{ base: "1fr", large: "2fr 3fr" }}>
        <Grid padding="20px" rowGap="5px" alignSelf="start">
          <div className="fs-5">{strings.fields.location.label}</div>
          {mapIsLoaded && (
            <GoogleMap
              mapContainerStyle={containerStyleMap}
              center={{
                lat: equipmentForm.coordsLat ?? centerMap.lat,
                lng: equipmentForm.coordsLng ?? centerMap.lng,
              }}
              zoom={15}
              // onLoad={onLoad}
              // onUnmount={onUnmount}
            >
              <Marker
                draggable
                onDragEnd={onChangeMapMarker}
                position={{
                  lat: equipmentForm.coordsLat ?? centerMap.lat,
                  lng: equipmentForm.coordsLng ?? centerMap.lng,
                }}
              ></Marker>
            </GoogleMap>
          )}
        </Grid>
        <ScrollView maxHeight="75vh">
          <Grid rowGap="15px" columnGap="15px" padding="20px">
            <Grid gap="15px" templateColumns={{ base: "1fr" }}>
              <TextField
                name="name"
                label={<LabelRequired text={strings.fields.name.label} />}
                value={equipmentForm.name}
                onChange={onChange}
                errorMessage={errors.name}
                hasError={!!errors.name}
              ></TextField>
              {/* <TextField
                name="location"
                label={<LabelRequired text={strings.fields.location.label} />}
                value={equipmentForm.location}
                onChange={onChange}
                errorMessage={errors.location}
                hasError={!!errors.location}
              ></TextField> */}
            </Grid>
            <TextAreaField
              name="description"
              label={strings.fields.description.label}
              value={equipmentForm.description}
              onChange={onChange}
            ></TextAreaField>
            <Grid
              templateColumns={{ base: "1fr", large: "2fr 2fr" }}
              rowGap="15px"
              columnGap="15px"
            >
              {type === "create" && (
                <Flex direction="column" gap="xs">
                  <Label htmlFor="companyID">
                    <LabelRequired text={strings.fields.company.label} />
                  </Label>
                  <Autocomplete
                    id="companyID"
                    name="companyID"
                    isRequired
                    placeholder={strings.fields.company.placeholder}
                    value={
                      companies.list.find(
                        (companyRec) =>
                          companyRec.id === equipmentForm.companyID
                      )?.name ?? equipmentForm.companyID
                    }
                    options={companies.list.map((companyRec) => ({
                      id: companyRec.id,
                      label: companyRec.name,
                    }))}
                    isLoading={companies.loading}
                    onClear={() => onClearSelect("companyID")}
                    onChange={onChangeSelect}
                    onSelect={({ id }) => onSelect("companyID", id)}
                    errorMessage={errors.companyID}
                    hasError={!!errors.companyID}
                    menuSlots={{ Empty: globalStrings.noResults }}
                  />
                </Flex>
              )}
              <Flex direction="column" gap="xs">
                <Label htmlFor="branchID">
                  <LabelRequired text={strings.fields.branch.label} />
                </Label>
                <Autocomplete
                  id="branchID"
                  name="branchID"
                  isRequired
                  disabled={!equipmentForm.companyID}
                  placeholder={strings.fields.branch.placeholder}
                  value={
                    branches.list.find(
                      (branchRec) => branchRec.id === equipmentForm.branchID
                    )?.name ?? equipmentForm.branchID
                  }
                  options={branches.list.map((branchRec) => ({
                    id: branchRec.id,
                    label: branchRec.name,
                  }))}
                  isLoading={branches.loading}
                  onClear={() =>
                    onClearSelect("branchID", equipmentForm.companyID)
                  }
                  onChange={onChangeSelect}
                  onSelect={({ id }) => onSelect("branchID", id)}
                  errorMessage={errors.branchID}
                  hasError={!!errors.branchID}
                  menuSlots={{ Empty: globalStrings.noResults }}
                />
              </Flex>
              <Flex direction="column" gap="xs">
                <Label htmlFor="deviceID">{strings.fields.device.label}</Label>
                <Autocomplete
                  id="deviceID"
                  name="deviceID"
                  disabled={!equipmentForm.branchID}
                  placeholder={strings.fields.device.placeholder}
                  value={
                    devices.list.find(
                      (deviceRec) => deviceRec.id === equipmentForm.deviceID
                    )?.name ||
                    equipmentForm.deviceID ||
                    ""
                  }
                  options={devices.list.map((deviceRec) => ({
                    id: deviceRec.id,
                    label: deviceRec.name,
                  }))}
                  isLoading={devices.loading}
                  onClear={() =>
                    onClearSelect("deviceID", equipmentForm.branchID)
                  }
                  onChange={onChangeSelect}
                  onSelect={({ id }) => onSelect("deviceID", id)}
                  errorMessage={errors.deviceID}
                  hasError={!!errors.deviceID}
                  menuSlots={{ Empty: globalStrings.noResults }}
                />
              </Flex>
              <Flex direction="column" gap="xs">
                <Label htmlFor="routineID">
                  {strings.fields.routine.label}
                </Label>
                <Autocomplete
                  id="routineID"
                  name="routineID"
                  disabled={!equipmentForm.companyID}
                  placeholder={strings.fields.routine.placeholder}
                  value={
                    routines.list.find(
                      (routineRec) => routineRec.id === equipmentForm.routineID
                    )?.name ||
                    equipmentForm.routineID ||
                    ""
                  }
                  options={routines.list.map((routineRec) => ({
                    id: routineRec.id,
                    label: routineRec.name,
                  }))}
                  isLoading={routines.loading}
                  onClear={() =>
                    onClearSelect("routineID", equipmentForm.companyID)
                  }
                  onChange={onChangeSelect}
                  onSelect={({ id }) => onSelect("routineID", id)}
                  errorMessage={errors.routineID}
                  hasError={!!errors.routineID}
                  menuSlots={{ Empty: globalStrings.noResults }}
                />
              </Flex>
              {companies.list.find(
                (companyRec) => companyRec.id === equipmentForm.companyID
              )?.id && (
                <SelectSpare
                  initialValues={equipmentForm.spares}
                  onChangeSpares={onChangeSpares}
                  parentName="equipmentID"
                  parentValue={equipmentForm.id}
                  companyID={equipmentForm.companyID}
                />
              )}
            </Grid>
            <Flex justifyContent="end">
              <Button
                children={strings.fields.buttons.reset}
                onClick={resetFormValues}
              ></Button>
              <Button
                children={
                  type === "create"
                    ? strings.fields.buttons.create
                    : strings.fields.buttons.save
                }
                onClick={onSubmitForm}
                variation="primary"
                isDisabled={Object.values(errors).some((e) => !!e)}
              ></Button>
            </Flex>
          </Grid>
        </ScrollView>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: {
        label: "Nombre",
      },
      location: {
        label: "Ubicación",
      },
      description: {
        label: "Descripción",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      branch: {
        label: "Sede",
        placeholder: "Buscar sede...",
      },
      device: {
        label: "Dispositivo",
        placeholder: "Buscar dispositivo...",
      },
      routine: {
        label: "Rutina de mantenimiento por defecto",
        placeholder: "Buscar rutina...",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
      errorFetchBranches: "Error al consultar sedes",
      errorFetchDevices: "Error al consultar dispositivos",
      errorFetchRoutines: "Error al consultar rutinas",
    },
  },
});

export default EquipmentForm;
